import React, { ChangeEvent, Dispatch, RefObject, SetStateAction, useState } from "react";
import {
  Stack,
  IconButton,
  Divider,
  FormGroup,
  FormControlLabel,
  Button,
  Box,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import {
  PanelLongPostAlignCenterSvg,
  PanelLongPostAlignLeftSvg,
  PanelLongPostAlignRightSvg,
  PanelLongPostAlignJustify,
  PanelLongPostBoldSvg,
  PanelLongPostItalicSvg,
  PanelLongPostUnderlineSvg,
} from "@/assets/svg/PanelLongPostSvg";
import ArrowUndoLongPostSvg from "@/assets/svg/ArrowUndoLongPostSvg";
import ArrowRedoLongPostSvg from "@/assets/svg/ArrowRedoLongPostSvg";
import { styles } from "./stylesEditorToolbar";
import CheckboxActiveSvg from "@/assets/svg/CheckboxActiveSvg";
import CheckboxInactiveSvg from "@/assets/svg/CheckboxInactiveSvg";

interface EditorToolbarProps {
  handleUndo: () => void;
  handleRedo: () => void;
  selectedSize: string;
  setSelectedSize: Dispatch<SetStateAction<string>>;
  applyFontSizeAndBlockData: (fontSize: string, alignment?: string) => void;
  applyHeading: (level: "h1" | "h2" | "paragraph") => void;
  fontSizeOptions: (number | string)[];
  toggleInlineStyle: (style: "BOLD" | "ITALIC" | "UNDERLINE") => void;
  toggleBlockData: (key: string, value: string) => void;
  isPaidContent: boolean;
  setIsPaidContent: (value: boolean) => void;
  fileInputEditorRef: RefObject<HTMLInputElement | null>;
  handleImageChange: (event: ChangeEvent<HTMLInputElement>) => void;
  invalidPrice?: boolean;
}

const EditorToolbar: React.FC<EditorToolbarProps> = ({
  handleUndo,
  handleRedo,
  // selectedSize,
  // setSelectedSize,
  // applyFontSizeAndBlockData,
  // fontSizeOptions,
  toggleInlineStyle,
  toggleBlockData,
  applyHeading,
  isPaidContent,
  setIsPaidContent,
  fileInputEditorRef,
  handleImageChange,
  // invalidPrice,
}) => {
  const [textStyle, setTextStyle] = useState<"h1" | "h2" | "paragraph">("paragraph");
  return (
    <>
      <Box sx={styles.containerBox}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={styles.toolbarStack}
        >
          <Stack direction="row" alignItems="center">
            <Stack direction="row" alignItems="center">
              <Box sx={styles.undoRedoWrapper}>
                <IconButton onClick={handleUndo} size="small">
                  <ArrowUndoLongPostSvg />
                </IconButton>
                <IconButton onClick={handleRedo} size="small">
                  <ArrowRedoLongPostSvg />
                </IconButton>
              </Box>

              <Divider orientation="vertical" flexItem sx={styles.divider} />

              <FormControl size="small" sx={styles.selectFormControl}>
                <Select
                  value={textStyle}
                  onChange={(e) => {
                    const level = e.target.value as "h1" | "h2" | "paragraph";
                    setTextStyle(level);
                    applyHeading(level);
                  }}
                  sx={styles.select}
                  MenuProps={{ PaperProps: { sx: styles.selectMenuProps } }}
                >
                  <MenuItem value="paragraph" sx={styles.menuItem}>
                    Paragraph
                  </MenuItem>
                  <MenuItem value="h1" sx={styles.menuItem}>
                    Heading 1
                  </MenuItem>
                  <MenuItem value="h2" sx={styles.menuItem}>
                    Heading 2
                  </MenuItem>
                </Select>
              </FormControl>

              <Divider orientation="vertical" flexItem sx={styles.divider} />

              <Stack sx={styles.inlineStyleButtons}>
                <IconButton onClick={() => toggleInlineStyle("BOLD")} size="small">
                  <PanelLongPostBoldSvg />
                </IconButton>
                <IconButton onClick={() => toggleInlineStyle("ITALIC")} size="small">
                  <PanelLongPostItalicSvg />
                </IconButton>
                <IconButton onClick={() => toggleInlineStyle("UNDERLINE")} size="small">
                  <PanelLongPostUnderlineSvg />
                </IconButton>
              </Stack>

              <Divider orientation="vertical" flexItem sx={styles.divider} />

              <Stack sx={styles.alignButtonsWrapper}>
                <IconButton
                  onClick={() => toggleBlockData("text-align", "left")}
                  size="small"
                  sx={styles.iconButton}
                >
                  <PanelLongPostAlignLeftSvg />
                </IconButton>
                <IconButton
                  onClick={() => toggleBlockData("text-align", "center")}
                  size="small"
                  sx={styles.iconButton}
                >
                  <PanelLongPostAlignCenterSvg />
                </IconButton>
                <IconButton
                  onClick={() => toggleBlockData("text-align", "right")}
                  size="small"
                  sx={styles.iconButton}
                >
                  <PanelLongPostAlignRightSvg />
                </IconButton>
                <IconButton
                  onClick={() => toggleBlockData("text-align", "justify")}
                  size="small"
                  sx={styles.iconButton}
                >
                  <PanelLongPostAlignJustify />
                </IconButton>
              </Stack>

              <Divider orientation="vertical" flexItem sx={styles.divider} />
            </Stack>

            <FormGroup>
              <FormControlLabel
                sx={styles.paidContentWrapper}
                control={
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsPaidContent(!isPaidContent);
                    }}
                    sx={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      p: 0,
                      "& svg": { width: "18px", height: "18px" },
                    }}
                  >
                    {isPaidContent ? <CheckboxActiveSvg /> : <CheckboxInactiveSvg />}
                  </Box>
                }
                label="Paid Content"
              />
            </FormGroup>
            <Button
              onClick={() => fileInputEditorRef.current?.click()}
              sx={styles.insertImageButton}
            >
              Insert Image
            </Button>
            <input
              type="file"
              ref={fileInputEditorRef}
              onChange={handleImageChange}
              style={{ display: "none" }}
              accept="image/*"
            />
          </Stack>
        </Stack>
      </Box>

      {/* {isPaidContent && invalidPrice && (
        <Typography color={theme.palette.error.main} variant="h5" sx={styles.errorText}>
          Price is required
        </Typography>
      )} */}
    </>
  );
};

export default EditorToolbar;
